import { Add, DeleteOutline } from '@mui/icons-material';
import { Button, CircularProgress, Dialog, IconButton, MenuItem, Select } from '@mui/material';
import { addDays, addMinutes, addMonths } from 'date-fns';
import he from 'date-fns/locale/he';
import { useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-datetime/css/react-datetime.css';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

registerLocale('he', he);

export type FutureAppointment = { time: Date; coupon?: string };

/**
 * Book SEVERAL FUTURE appointments at once (GH-539/625). Deferred payment: for each one
 * the member gets a payment link ~48h before the session; unpaid → auto-cancelled.
 * Minimum 2 meetings — a single meeting should use the regular "פגישה נוספת" flow.
 */
type FutureAppointmentsDialogProps = {
    title: string;
    duration?: number;
    onScheduleButtonPressed: (appointments: FutureAppointment[]) => void;
    service?: any;
    open: boolean;
    onClose: () => void;
    loading?: boolean;
};

type Inputs = { duration: number; meetings: { datetime: Date | null; coupon: string }[] };
const MIN_MEETINGS = 2;
const MAX_MEETINGS = 8; // matches the backend cap on open future appointments per member

// frequency options for the recurring-series generator
const FREQUENCIES: { label: string; addFn: (d: Date, n: number) => Date }[] = [
    { label: 'כל שבוע', addFn: (d, n) => addDays(d, 7 * n) },
    { label: 'כל שבועיים', addFn: (d, n) => addDays(d, 14 * n) },
    { label: 'כל חודש', addFn: (d, n) => addMonths(d, n) },
];

const FutureAppointmentsDialog = (props: FutureAppointmentsDialogProps) => {
    const { register, control, formState, handleSubmit } = useForm<Inputs>({
        mode: 'onChange',
        defaultValues: {
            duration: props.duration || 20,
            meetings: [
                { datetime: null, coupon: '' },
                { datetime: null, coupon: '' },
            ],
        },
    });
    const { fields, append, remove, replace } = useFieldArray({ control, name: 'meetings' });

    // recurring-series generator (local — not part of the submitted form)
    const [seriesStart, setSeriesStart] = useState<Date | null>(null);
    const [seriesFreq, setSeriesFreq] = useState(0);
    const [seriesCount, setSeriesCount] = useState(4);

    const generateSeries = () => {
        if (!seriesStart) {
            alert('בחרו מועד לפגישה הראשונה');
            return;
        }
        const count = Math.max(MIN_MEETINGS, Math.min(MAX_MEETINGS, seriesCount));
        const addFn = FREQUENCIES[seriesFreq].addFn;
        const rows = Array.from({ length: count }, (_, i) => ({ datetime: addFn(seriesStart, i), coupon: '' }));
        replace(rows); // fill the meeting rows — each stays individually editable
    };

    const formSubmitHandler = (data: Inputs) => {
        const appointments: FutureAppointment[] = [];
        for (const m of data.meetings) {
            if (!m.datetime) continue;
            if (m.datetime < new Date()) {
                alert('לא ניתן לקבוע פגישה בעבר');
                return;
            }
            appointments.push({ time: m.datetime, coupon: m.coupon || undefined });
        }
        if (appointments.length < MIN_MEETINGS) {
            alert(`יש להזין לפחות ${MIN_MEETINGS} מועדים`);
            return;
        }
        props.onScheduleButtonPressed(appointments);
    };

    return (
        <Dialog open={props.open} onClose={props.onClose} className='overflow-visible' PaperProps={{ style: { borderRadius: 10 } }}>
            <div dir='rtl' className='py-4 min-h-[450px] w-[480px] text-center flex flex-col gap-4 overflow-visible'>
                <div>
                    <h2 className='text-lg font-bold'>{props.title}</h2>
                    <h3>{props.service?.title.he}</h3>
                </div>
                <div className='flex flex-col justify-between h-full gap-4'>
                    <div className='text-center py-1 px-8 flex flex-col gap-5'>
                        {/* one shared duration */}
                        <div className='flex flex-row gap-4 items-center flex-1'>
                            <span className='font-medium w-40 text-start'>משך המפגש</span>
                            <span className='flex items-center gap-4 w-full'>
                                <input
                                    type='number'
                                    step={5}
                                    style={{ width: '100%', borderRadius: 10, borderWidth: 1, padding: 4, textAlign: 'center', fontFamily: 'Open Sans' }}
                                    min={10}
                                    max={120}
                                    {...register('duration')}
                                />
                                <span className='px-1 flex flex-1 w-full'>{'דקות'}</span>
                            </span>
                        </div>

                        {/* recurring-series quick generator */}
                        <div className='flex flex-col gap-2 border border-blue-200 rounded-lg p-3 bg-blue-50 text-sm'>
                            <span className='font-semibold text-start'>מילוי מהיר — סדרה קבועה</span>
                            <div className='flex flex-row gap-2 items-center'>
                                <label className='w-24 text-start'>פגישה ראשונה</label>
                                <DatePicker
                                    popperProps={{ dir: 'rtl', positionFixed: true, strategy: 'fixed' } as any}
                                    timeCaption='שעה'
                                    timeIntervals={5}
                                    wrapperClassName='w-full'
                                    customInput={<input style={{ textAlign: 'center', width: '100%', borderWidth: 1, borderRadius: 10, paddingBlock: 4, fontFamily: 'Open Sans' }} />}
                                    dateFormat={'dd/MM/yy HH:mm'}
                                    timeFormat='HH:mm'
                                    locale={'he'}
                                    showTimeSelect
                                    selected={seriesStart}
                                    onChange={setSeriesStart}
                                    minDate={addMinutes(new Date(), 20)}
                                    placeholderText='בחרו תאריך ושעה'
                                />
                            </div>
                            <div className='flex flex-row gap-2 items-center'>
                                <label className='w-24 text-start'>תדירות</label>
                                <Select size='small' value={seriesFreq} onChange={(e) => setSeriesFreq(Number(e.target.value))} className='flex-1 bg-white' MenuProps={{ dir: 'rtl' }}>
                                    {FREQUENCIES.map((f, i) => (
                                        <MenuItem key={i} value={i}>{f.label}</MenuItem>
                                    ))}
                                </Select>
                                <label className='w-16 text-start'>כמות</label>
                                <input
                                    type='number'
                                    min={MIN_MEETINGS}
                                    max={MAX_MEETINGS}
                                    value={seriesCount}
                                    onChange={(e) => setSeriesCount(Number(e.target.value))}
                                    style={{ width: 60, borderRadius: 10, borderWidth: 1, padding: 4, textAlign: 'center', fontFamily: 'Open Sans' }}
                                />
                            </div>
                            <Button variant='contained' size='small' onClick={generateSeries}>
                                צרו סדרה
                            </Button>
                            <span className='text-xs text-gray-500'>{`עד ${MAX_MEETINGS} פגישות. ניתן לערוך כל מועד בנפרד אחרי היצירה.`}</span>
                        </div>

                        {/* one row per meeting: date + optional coupon */}
                        {fields.map((field, index) => (
                            <div key={field.id} className='flex flex-col gap-2 border border-gray-200 rounded-lg p-3 bg-gray-50'>
                                <div className='flex flex-row items-center justify-between'>
                                    <span className='font-medium text-sm'>{`פגישה ${index + 1}`}</span>
                                    {fields.length > MIN_MEETINGS && (
                                        <IconButton size='small' color='error' onClick={() => remove(index)} title='הסרה'>
                                            <DeleteOutline fontSize='small' />
                                        </IconButton>
                                    )}
                                </div>
                                <div className='flex flex-row gap-4 items-center'>
                                    <label className='font-medium text-start w-28 text-sm'>מועד</label>
                                    <Controller
                                        control={control}
                                        name={`meetings.${index}.datetime` as const}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <DatePicker
                                                popperProps={{ dir: 'rtl', positionFixed: true, strategy: 'fixed' } as any}
                                                timeCaption='שעה'
                                                timeIntervals={5}
                                                wrapperClassName='w-full'
                                                customInput={
                                                    <input
                                                        style={{ textAlign: 'center', width: '100%', borderWidth: 1, borderRadius: 10, paddingBlock: 4, fontFamily: 'Open Sans' }}
                                                    />
                                                }
                                                dateFormat={'dd/MM/yy HH:mm'}
                                                timeFormat='HH:mm'
                                                locale={'he'}
                                                showTimeSelect
                                                selected={field.value}
                                                onChange={field.onChange}
                                                minDate={addMinutes(new Date(), 20)}
                                            />
                                        )}
                                    />
                                </div>
                                <div className='flex flex-row gap-4 items-center'>
                                    <label className='font-medium text-start w-28 text-sm'>קופון</label>
                                    <span style={{ borderRadius: 10, borderWidth: 1, padding: 4, textAlign: 'center', fontFamily: 'Open Sans', width: '100%' }}>
                                        <input {...register(`meetings.${index}.coupon` as const)} type='text' placeholder='קוד קופון (אופציונלי)' className='w-full text-center' />
                                    </span>
                                </div>
                            </div>
                        ))}

                        {fields.length < MAX_MEETINGS && (
                            <Button variant='outlined' size='small' startIcon={<Add />} onClick={() => append({ datetime: null, coupon: '' })}>
                                הוספת פגישה
                            </Button>
                        )}
                    </div>

                    <div className='flex flex-col items-center gap-2 px-1'>
                        <Button variant='contained' disabled={!formState.isValid || props.loading} onClick={handleSubmit(formSubmitHandler)}>
                            {`קביעת ${fields.length} פגישות`}
                        </Button>
                        <span className='text-xs leading-snug font-base whitespace-pre-wrap'>
                            {'לכל פגישה בתשלום יישלח למנוי קישור תשלום כ-48 שעות לפני מועדה.\nללא תשלום עד המועד — הפגישה תבוטל אוטומטית.\nמפגש ללא עלות נקבע מיידית.'}
                        </span>
                        {props.loading && <CircularProgress />}
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default FutureAppointmentsDialog;
